import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedFooter from '../common/UnifiedFooter';
import EcommerceCartDrawer from '../ecommerce/EcommerceCartDrawer';
import EcommerceAuthModal from '../ecommerce/EcommerceAuthModal';
import EcommerceWhatsAppFloating from '../ecommerce/EcommerceWhatsAppFloating';
import EcommerceCheckoutModal from '../ecommerce/EcommerceCheckoutModal';
import { useWebStore } from '../../context/WebStoreContext';

const DEFAULT_NOVEDADES_POSTS = [
  {
    id: 'nov-1',
    title: 'Línea de Moldes para Roscas y Repostería',
    badge: 'LANZAMIENTO DE TEMPORADA',
    badgeColor: 'warning',
    date: 'Temporada Actual',
    image: '/images/novedades/moldes_roscas.jpg',
    description: 'Moldes descartables especiales para roscas de pascua, pan dulce y repostería artesanal e industrial. Variedad de diámetros y gramajes con máxima resistencia al horneado.',
    bulletPoints: [
      'Fabricados con papel microcorrugado apto para cocción',
      'Precios especiales por bulto cerrado y pallets',
      'Stock permanente con entrega inmediata'
    ],
    rubroTarget: 'Gastronomia',
    whatsappMessage: '¡Hola! Me comunico para consultar por la oferta de Moldes para Roscas que vi en Novedades.'
  },
  {
    id: 'nov-2',
    title: 'Ofertas Especiales en Bandejas, Bolsas y Descartables',
    badge: 'PROMO DESTACADA',
    badgeColor: 'danger',
    date: 'Oferta Vigente',
    image: '/images/novedades/ofertas_temporada.jpg',
    description: 'Promociones directas de fábrica en bandejas de cartón, blondas, bolsas de polietileno de alta y baja densidad, rollos de arranque y films adherentes.',
    bulletPoints: [
      'Descuentos por escala para distribuidoras y comercios',
      'Envíos a todo el país y retiro inmediato en planta',
      'Asesoramiento comercial personalizado'
    ],
    rubroTarget: 'Bolsas',
    whatsappMessage: '¡Hola! Me comunico para consultar por las ofertas especiales de Bandejas y Bolsas que vi en Novedades.'
  },
  {
    id: 'nov-3',
    title: 'Vasos Térmicos, Pasta y Línea Descartable',
    badge: 'STOCK PERMANENTE',
    badgeColor: 'success',
    date: 'Disponibilidad Inmediata',
    image: '/images/novedades/vasos_pasta.jpg',
    description: 'Soluciones integrales para cafeterías, heladerías, eventos y gastronomía. Vasos térmicos, potes de pasta, cubiertos y complementos descartables de máxima calidad.',
    bulletPoints: [
      'Línea completa de potes y vasos térmicos',
      'Venta mayorista y minorista con precios de fábrica',
      'Consultá por listas diferenciales'
    ],
    rubroTarget: 'Gastronomia',
    whatsappMessage: '¡Hola! Me comunico para consultar por la línea de Vasos Térmicos y Descartables que vi en Novedades.'
  }
];

const NovedadesPage = ({ user, onLogout }) => {
  const { storeConfig, landingConfig: globalLandingConfig, rubros, ecomCustomer: contextCustomer, setEcomCustomer: setContextCustomer, logoutCustomer } = useWebStore();
  const [config, setConfig] = useState(globalLandingConfig || null);
  const [selectedFlyer, setSelectedFlyer] = useState(null);

  useEffect(() => {
    if (globalLandingConfig) {
      setConfig(globalLandingConfig);
    }
  }, [globalLandingConfig]);

  // Estados de Carrito y Autenticación
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const [ecomCustomer, setEcomCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Mantener ecomCustomer sincronizado con el contexto global
  useEffect(() => {
    setEcomCustomer(contextCustomer || null);
  }, [contextCustomer]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const totalCartCount = cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0);
  const totalCartPrice = cartItems.reduce((acc, item) => acc + ((Number(item.unitPrice) || 0) * (Number(item.quantity) || 1)), 0);
  const activeCustomer = ecomCustomer || (user?.email ? { d_razon: user.username, d_email: user.email } : null);
  const canSeePrices = Boolean(storeConfig?.commercial?.publicPricesEnabled || activeCustomer);

  const whatsappNumber = (config?.contactSection?.whatsapp || config?.topBar?.whatsapp || storeConfig?.footer?.whatsapp || '5491142476622').replace(/\D/g, '');

  const posts = (config?.novedadesPosts && config.novedadesPosts.length > 0) ? config.novedadesPosts : DEFAULT_NOVEDADES_POSTS;

  return (
    <div className="webstore-site-wrapper min-vh-100 d-flex flex-column" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', backgroundColor: 'var(--ecom-bg, #f8fafc)', color: 'var(--ecom-text, #1e293b)' }}>
      
      {/* Cabecera Unificada */}
      <UnifiedHeader
        activePage="novedades"
        cartCount={totalCartCount}
        cartTotal={totalCartPrice}
        currentUser={activeCustomer}
        onOpenCart={() => setIsCartOpen(true)}
        onLogout={() => {
          if (logoutCustomer) {
            logoutCustomer(true);
          } else {
            localStorage.removeItem('sigecom_ecom_customer');
            localStorage.removeItem('sigecom_ecom_last_activity');
            setEcomCustomer(null);
          }
        }}
        onAuthClick={(mode) => {
          setAuthModalMode(mode || 'register');
          setIsAuthModalOpen(true);
        }}
        priceListInfo="Lista Minorista"
        canSeePrices={canSeePrices}
        topBarConfig={config?.topBar}
        storeConfig={storeConfig}
        rubros={rubros}
      />

      {/* Banner Superior */}
      <div 
        className="text-white py-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, var(--ecom-primary, #0d6efd) 0%, var(--ecom-secondary, #0a58ca) 100%)'
        }}
      >
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
            <div>
              <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill text-uppercase mb-2">
                Promociones & Temporadas
              </span>
              <h1 className="fw-black fs-2 mb-0">Novedades y Ofertas</h1>
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="/" className="text-white opacity-75 text-decoration-none">Inicio</a></li>
                <li className="breadcrumb-item active text-white fw-bold" aria-current="page">Novedades</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Feed de Novedades y Ofertas (Posteos / Flyers) */}
      <main className="container flex-grow-1 py-4">
        
        <div className="row justify-content-center">
          <div className="col-lg-10 col-12">
            
            <div className="d-flex flex-column gap-5">
              {posts.map((post, idx) => (
                <article 
                  key={post.id || idx} 
                  className="card border rounded-4 shadow-sm overflow-hidden bg-white"
                  style={{ transition: 'box-shadow 0.3s ease' }}
                >
                  {/* Encabezado del Post */}
                  <div className="card-header bg-light border-bottom p-3 p-md-4 d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className={`badge ${post.badgeColor === 'danger' ? 'bg-danger' : (post.badgeColor === 'success' ? 'bg-success' : 'bg-warning text-dark')} fw-bold px-3 py-1.5 rounded-pill text-uppercase`}>
                        {post.badge || 'NOVEDAD'}
                      </span>
                      {post.date && (
                        <span className="text-muted small fw-semibold">
                          <i className="bi bi-calendar3 me-1"></i> {post.date}
                        </span>
                      )}
                    </div>
                    <span className="badge bg-white text-primary border border-primary-subtle fw-bold px-3 py-1 rounded-pill">
                      Hiperpel Oficial
                    </span>
                  </div>

                  {/* Flyer / Imagen Principal del Posteo */}
                  {post.image && (
                    <div className="position-relative bg-light text-center cursor-pointer" onClick={() => setSelectedFlyer(post)}>
                      <img
                        src={post.image}
                        alt={post.title}
                        className="img-fluid w-100"
                        style={{ maxHeight: '680px', objectFit: 'contain', cursor: 'zoom-in', backgroundColor: '#f8fafc' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                      <button 
                        type="button" 
                        className="btn btn-dark btn-sm bg-opacity-75 rounded-pill position-absolute bottom-0 end-0 m-3 px-3 shadow"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFlyer(post);
                        }}
                      >
                        <i className="bi bi-zoom-in me-1"></i> Ampliar Flyer
                      </button>
                    </div>
                  )}

                  {/* Cuerpo del Post */}
                  <div className="card-body p-4 p-md-5">
                    <h2 className="card-title fw-bold text-dark mb-3 fs-3">
                      {post.title}
                    </h2>
                    
                    <p className="card-text text-secondary fs-6 mb-4 lh-base">
                      {post.description}
                    </p>

                    {post.bulletPoints && post.bulletPoints.length > 0 && (
                      <div className="bg-light p-3 p-md-4 rounded-3 border mb-4">
                        <h6 className="fw-bold text-dark mb-2">Detalles de la Oferta:</h6>
                        <ul className="list-unstyled mb-0 d-flex flex-column gap-1.5 small text-muted">
                          {post.bulletPoints.map((pt, i) => (
                            <li key={i} className="d-flex align-items-start gap-2">
                              <i className="bi bi-check-circle-fill text-success fs-6 mt-0.5"></i>
                              <span>{pt}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Botones de Acción */}
                    <div className="d-flex flex-wrap align-items-center gap-3 pt-2">
                      <a
                        href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(post.whatsappMessage || `Hola! Me comunico para consultar por la novedad: ${post.title}`)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-success btn-lg rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2"
                      >
                        <i className="bi bi-whatsapp fs-5"></i>
                        <span>Consultar Oferta por WhatsApp</span>
                      </a>

                      <a
                        href={post.rubroTarget ? `/tienda?rubro=${encodeURIComponent(post.rubroTarget)}` : '/tienda'}
                        className="btn btn-outline-primary btn-lg rounded-pill px-4 fw-semibold d-flex align-items-center gap-2"
                      >
                        <i className="bi bi-bag-check"></i>
                        <span>Ver Rubro en Catálogo</span>
                      </a>
                    </div>
                  </div>

                </article>
              ))}
            </div>

            {/* Banner de Contacto para Nuevas Ofertas */}
            <div className="mt-5 p-4 p-lg-5 bg-primary text-white rounded-4 shadow-sm text-center">
              <h3 className="fw-bold mb-2">¿Querés recibir nuestras listas de ofertas y novedades periódicamente?</h3>
              <p className="lead fs-6 text-light mb-4 opacity-90">
                Escribinos por WhatsApp para sumarte a nuestro canal de difusión comercial mayorista.
              </p>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola! Deseo recibir las novedades y ofertas de Hiperpel por WhatsApp.')}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-warning btn-lg rounded-pill px-5 fw-bold text-dark shadow-sm d-inline-flex align-items-center gap-2"
              >
                <i className="bi bi-whatsapp fs-5"></i>
                <span>Suscribirme a Novedades</span>
              </a>
            </div>

          </div>
        </div>

      </main>

      {/* Footer Unificado */}
      <UnifiedFooter storeConfig={storeConfig} landingConfig={config} />

      {/* Modal Lightbox para Ampliar Flyer */}
      {selectedFlyer && (
        <Modal
          show={Boolean(selectedFlyer)}
          onHide={() => setSelectedFlyer(null)}
          size="xl"
          centered
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fs-5 fw-bold text-dark">
              {selectedFlyer.title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-2 p-md-3 text-center">
            <img
              src={selectedFlyer.image}
              alt={selectedFlyer.title}
              className="img-fluid rounded-3 shadow-sm"
              style={{ maxHeight: '85vh', objectFit: 'contain' }}
            />
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0 justify-content-between">
            <span className="text-muted small">{selectedFlyer.badge}</span>
            <div className="d-flex gap-2">
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(selectedFlyer.whatsappMessage || `Hola! Consulto por ${selectedFlyer.title}`)}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-success rounded-pill px-3 fw-bold btn-sm"
              >
                <i className="bi bi-whatsapp me-1"></i> Consultar por WhatsApp
              </a>
              <button
                type="button"
                className="btn btn-secondary rounded-pill px-3 btn-sm"
                onClick={() => setSelectedFlyer(null)}
              >
                Cerrar
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {/* Drawer de Carrito */}
      <EcommerceCartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={(key, qty) => {
          if (qty <= 0) setCartItems(prev => prev.filter(i => i.cartKey !== key));
          else setCartItems(prev => prev.map(i => i.cartKey === key ? { ...i, quantity: qty } : i));
        }}
        onRemoveItem={(key) => setCartItems(prev => prev.filter(i => i.cartKey !== key))}
        onClearCart={() => setCartItems([])}
        onCheckout={() => {
          setIsCartOpen(false);
          setIsCheckoutOpen(true);
        }}
        canSeePrices={canSeePrices}
        onLoginClick={() => {
          setIsCartOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
      />

      <EcommerceCheckoutModal
        show={isCheckoutOpen}
        onHide={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        canSeePrices={canSeePrices}
        currentUser={activeCustomer}
        whatsappSalesNumber={whatsappNumber}
        companyName={storeConfig?.footer?.companyName || 'HIPERPEL S.A.'}
        storeConfig={storeConfig}
        companyId={storeConfig?.company_id || 1}
        onRequireAuth={() => {
          setIsCheckoutOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
        onOrderCompleted={() => setCartItems([])}
      />

      <EcommerceAuthModal
        show={isAuthModalOpen}
        onHide={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onSuccess={(customer) => {
          setEcomCustomer(customer);
          if (setContextCustomer) {
            setContextCustomer(customer);
          } else {
            localStorage.setItem('sigecom_ecom_customer', JSON.stringify(customer));
            localStorage.setItem('sigecom_ecom_last_activity', Date.now().toString());
          }
          setIsAuthModalOpen(false);
        }}
        companyId={storeConfig?.company_id || 1}
      />

      {config?.whatsappFloating?.enabled !== false && (
        <EcommerceWhatsAppFloating
          phoneNumber={config?.whatsappFloating?.phone || whatsappNumber}
          defaultMessage={config?.whatsappFloating?.message || `¡Hola! Me comunico desde la sección de novedades de la web de ${config?.logoText || 'la empresa'}.`}
          showPill={config?.whatsappFloating?.showPill !== false}
          pillText={config?.whatsappFloating?.pillText || '¿Dudas? ¡Escribinos!'}
        />
      )}

    </div>
  );
};

export default NovedadesPage;
